import { Injectable, inject } from '@angular/core';
import { ContactController } from '@modules/contacts/shared/controllers/contact.controller';
import { ProjectCreatorEntitySidenavController } from '@modules/project-creator/shared/controllers/project-creator-entity-sidenav.controller';
import { SnackBarService } from '@core/services/snackbar.service';
import { NavigateService } from '@shared/services/navigate.service';
import { ChangesSidenavController } from '@shared/modules/task-sidenav/controllers/changes-sidenav.controller';
import { TaskSidenavController } from '@shared/modules/task-sidenav/controllers/task-sidenav.controller';
import { TaskService } from '@shared/modules/tasks/services/task-service';
import { TaskType } from '@shared/enums/task-type.enum';
import { take } from 'rxjs';
import { ProjectLogbookRowAction } from '@modules/projects/modules/project-summary-v2/components/project-summary-logbook/project-summary-logbook.helper';
import { ProjectSiteDiaryEntrySidenavComponent } from '@modules/projects/modules/project-site-diary-entry-sidenav/project-site-diary-entry-sidenav.component';
import { ProcessMessageTaskSidenavComponent } from '@modules/projects/modules/project-dashboard/pages/project-stage-tasks/components/process-message-task-sidenav/process-message-task-sidenav.component';

@Injectable({ providedIn: 'root' })
export class ProjectLogNavigationService {
  private readonly navigateService = inject(NavigateService);
  private readonly taskService = inject(TaskService);
  private readonly snackBarService = inject(SnackBarService);
  private readonly taskSidenavController = new TaskSidenavController();

  open(action: ProjectLogbookRowAction | null | undefined): void {
    if (!action) return;

    switch (action.type) {
      case 'contact':
        new ContactController().preview(action.contactId);
        return;
      case 'project':
        this.navigateService.goWithPromise('project-dashboard-summary', { projectId: action.projectId });
        return;
      case 'entry':
        new ProjectCreatorEntitySidenavController().editProjectEntity({
          id: action.projectId,
          entryId: action.entryId,
          allowImportant: true
        });
        return;
      case 'site_diary_entry':
        this.taskSidenavController.sidenav.open(
          ProjectSiteDiaryEntrySidenavComponent,
          {
            maxWidth: '611px',
            class: 'no-padding',
            hideBackdrop: true,
            hardReload: true,
            closeOnBackdrop: true
          },
          { entryId: action.entryId, replaceUrl: true }
        );
        return;
      case 'configurator':
        this.navigateService.go('project-dashboard-configurator', { projectId: action.projectId }, {
          queryParams: { tool: 'configuration', step: 1 }
        });
        return;
      case 'team':
        this.navigateService.go('project-dashboard-team', { projectId: action.projectId });
        return;
      case 'change':
        new ChangesSidenavController().previewChange(String(action.taskId));
        return;
      case 'task':
        this.openTask(action.taskId);
        return;
      default:
        return;
    }
  }

  private openTask(taskId: number): void {
    this.taskService
      .getTask(taskId)
      ?.pipe(take(1))
      .subscribe({
        next: (task) => {
          if (!task?.id && !task?.taskId) {
            this.snackBarService.defaultError();
            return;
          }

          if (task.type === TaskType.TYPE_CHANGE) {
            new ChangesSidenavController().previewChange(String(task.taskId || task.id));
            return;
          }

          if (
            !!task?.project?.id &&
            (task.type === TaskType.TYPE_MESSAGE || task.type === TaskType.TYPE_FOLLOW_UP)
          ) {
            this.taskSidenavController.sidenav.open(
              ProcessMessageTaskSidenavComponent,
              this.taskSidenavController.getWideSidenavConfig(),
              {
                mode: 'send',
                projectId: task.project.id,
                stage: task?.project?.stage as any,
                sortOrder: Number(task.sortOrder || 1),
                parentId: task.parentId ?? null,
                groupId: task.taskGroup?.id || null,
                mapperTaskId: task.id,
                taskType: task.type,
                task
              }
            );
            return;
          }

          new TaskSidenavController().previewTask(String(task.taskId || task.id), {
            type: task.type || TaskType.TYPE_TASK
          });
        },
        error: () => {
          this.snackBarService.defaultError();
        }
      });
  }
}
